<template>
  <div class="animate-fade-in-up font-NotoSerifKhmer">
    <!-- Dashboard Header -->
    <header class="mb-10 flex flex-col md:flex-row justify-between items-end gap-6">
      <div>
        <h1 class="text-4xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-white/70 mb-2 drop-shadow-sm">
          {{ t('Dashboard') }}
        </h1>
        <p class="text-white/80 text-lg font-light">{{ t("Welcome back! Here's your wedding overview.") }}</p>
      </div>
      <div class="hidden md:block">
          <div class="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 text-white text-sm backdrop-blur-md">
            <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            {{ t('Real-time Monitoring') }}
          </div>
      </div>
    </header>

    <!-- Stats Grid -->
    <section class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
      <!-- Total Events -->
      <div 
        @click="$router.push({ name: 'events' })"
        class="group cursor-pointer bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 p-6 transition-all duration-300 hover:bg-white/20 hover:scale-[1.02] hover:shadow-[0_10px_40px_rgba(0,0,0,0.2)]"
      >
        <div class="flex justify-between items-start mb-4">
          <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
            📅
          </div>
          <div class="px-2.5 py-1 rounded-lg bg-white/10 text-white/90 text-xs font-medium border border-white/10">{{ t('Events') }}</div>
        </div>
        <div class="text-4xl font-bold text-white mb-1">{{ totalEvents }}</div>
        <div class="text-white/60 text-sm font-medium uppercase tracking-wider">{{ t('Total Events') }}</div>
      </div>

      <!-- Total Guests -->
      <div class="bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 p-6 transition-all duration-300 hover:bg-white/15 hover:-translate-y-1">
        <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center text-2xl shadow-lg mb-4">
          👥
        </div>
        <div class="text-4xl font-bold text-white mb-1">{{ totalGuests }}</div>
        <div class="text-white/60 text-sm font-medium uppercase tracking-wider">{{ t('Total Guests') }}</div>
      </div>

      <!-- Revenue (USD) -->
      <div class="bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 p-6 transition-all duration-300 hover:bg-white/15 hover:-translate-y-1">
        <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-2xl shadow-lg mb-4 text-white">
          <i class="fa-solid fa-hand-holding-dollar"></i>
        </div>
        <div class="text-4xl font-bold text-emerald-400 mb-1">
          ${{ numeralFormat(revenueUSD, '0,0[.]00') }}
        </div>
        <div class="text-white/60 text-sm font-medium uppercase tracking-wider">{{ t('Total Revenue (USD)') }}</div>
      </div>

      <!-- Revenue (KHR) -->
      <div class="bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 p-6 transition-all duration-300 hover:bg-white/15 hover:-translate-y-1">
        <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-2xl shadow-lg mb-4 text-white">
          <i class="fa-solid fa-money-bill-khmer"></i>
        </div>
        <div class="text-4xl font-bold text-amber-400 mb-1">
          ៛{{ numeralFormat(revenueKHR, '0,0') }}
        </div>
        <div class="text-white/60 text-sm font-medium uppercase tracking-wider">{{ t('Total Revenue (KHR)') }}</div>
      </div>
    </section>

    <!-- Financial Breakdown & Recommendations -->
    <section class="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
      <!-- Expense Summary -->
      <div class="lg:col-span-2 bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8">
        <div class="flex justify-between items-center mb-8">
          <h3 class="text-2xl font-bold text-white">{{ t('Expense Overview') }}</h3>
          <div class="flex gap-2">
            <span class="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-xs font-bold border border-red-500/30 uppercase tracking-widest">{{ t('Costs tracked') }}</span>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Spent USD -->
          <div class="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
            <div class="flex justify-between items-start mb-4">
              <span class="text-white/50 text-xs font-black uppercase tracking-widest">{{ t('Spent USD') }}</span>
              <span class="text-red-400 text-xs font-bold font-mono">-${{ numeralFormat(expensesUSD, '0,0[.]00') }}</span>
            </div>
            <div class="text-3xl font-black text-white">
              ${{ numeralFormat(expensesUSD, '0,0[.]00') }}
            </div>
            <div class="mt-4 h-1.5 bg-white/5 rounded-full overflow-hidden">
               <div class="h-full bg-red-400" :style="{ width: budgetEfficiencyUSD + '%' }"></div>
            </div>
          </div>

          <!-- Spent KHR -->
          <div class="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
            <div class="flex justify-between items-start mb-4">
              <span class="text-white/50 text-xs font-black uppercase tracking-widest">{{ t('Spent KHR') }}</span>
              <span class="text-red-400 text-xs font-bold font-mono">-៛{{ numeralFormat(expensesKHR, '0,0') }}</span>
            </div>
            <div class="text-3xl font-black text-white">
              ៛{{ numeralFormat(expensesKHR, '0,0') }}
            </div>
             <div class="mt-4 h-1.5 bg-white/5 rounded-full overflow-hidden">
               <div class="h-full bg-red-400" :style="{ width: budgetEfficiencyKHR + '%' }"></div>
            </div>
          </div>
        </div>

        <div class="mt-8 pt-8 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-8">
           <div>
             <h4 class="text-white/40 text-xs font-black uppercase tracking-widest mb-4">{{ t('Financial Status (USD)') }}</h4>
             <div class="flex items-end gap-3">
                <div class="text-4xl font-black" :class="profitUSD >= 0 ? 'text-emerald-400' : 'text-red-400'">
                  ${{ numeralFormat(profitUSD, '0,0[.]00') }}
                </div>
                <div class="text-white/60 text-sm mb-1 font-medium">{{ t('Net Profit') }}</div>
             </div>
           </div>
           <div>
             <h4 class="text-white/40 text-xs font-black uppercase tracking-widest mb-4">{{ t('Financial Status (KHR)') }}</h4>
             <div class="flex items-end gap-3">
                <div class="text-4xl font-black" :class="profitKHR >= 0 ? 'text-amber-400' : 'text-red-400'">
                  ៛{{ numeralFormat(profitKHR, '0,0') }}
                </div>
                <div class="text-white/60 text-sm mb-1 font-medium">{{ t('Net Profit') }}</div>
             </div>
           </div>
        </div>
      </div>

      <!-- Quick Actions / Upcoming -->
      <div class="bg-gradient-to-br from-indigo-500/20 to-purple-600/20 backdrop-blur-xl rounded-3xl border border-white/20 p-8">
        <h3 class="text-2xl font-bold text-white mb-6">{{ t('Quick Overview') }}</h3>
        <div class="space-y-6">
          <div class="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all cursor-pointer" @click="$router.push({ name: 'events' })">
            <div class="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center text-pink-300 text-lg">
              <i class="fa-solid fa-calendar-check"></i>
            </div>
            <div>
              <div class="text-white font-bold">{{ t('Planned Events') }}</div>
              <div class="text-white/50 text-xs">{{ totalEvents }} {{ t('items scheduled') }}</div>
            </div>
          </div>

          <div class="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
            <div class="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-300 text-lg">
              <i class="fa-solid fa-users-viewfinder"></i>
            </div>
            <div>
              <div class="text-white font-bold">{{ t('Guest List') }}</div>
              <div class="text-white/50 text-xs">{{ totalGuests }} {{ t('confirmed guests') }}</div>
            </div>
          </div>

          <div class="mt-8">
            <div class="text-white/40 text-[10px] font-black uppercase tracking-[0.2em] mb-4">{{ t('Recent Events') }}</div>
            <div class="space-y-3">
              <div v-for="event in events.slice(0, 2)" :key="event.id" class="flex justify-between items-center text-sm p-3 bg-white/5 rounded-xl">
                 <span class="text-white font-medium truncate max-w-[120px]">{{ event.eventName }}</span>
                 <span class="text-indigo-300 text-xs">{{ formatDate(event.eventStartDate) }}</span>
              </div>
              <div v-if="events.length === 0" class="text-white/20 text-center py-4 text-xs italic">
                {{ t('No events created yet') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { getCollectionQuery } from '@/composables/getCollection'
import { where, orderBy } from 'firebase/firestore'
import { projectAuth } from '@/firebase/config'
import { formatDate2 ,formatDate} from '@/composables/formatDate'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const events = ref([])
const guests = ref([])
const expenses = ref([])

const totalEvents = computed(() => events.value.length)

const totalGuests = computed(() => guests.value.length)

// Revenue Calculations
const revenueUSD = computed(() => {
  return guests.value
    .filter(g => g.currency === 'USD')
    .reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0)
})

const revenueKHR = computed(() => {
  return guests.value
    .filter(g => g.currency === 'KHR')
    .reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0)
})

// Expense Calculations
const expensesUSD = computed(() => {
  return expenses.value
    .filter(e => e.currency === 'USD')
    .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
})

const expensesKHR = computed(() => {
  return expenses.value
    .filter(e => e.currency === 'KHR')
    .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
})

// Profit Calculations
const profitUSD = computed(() => revenueUSD.value - expensesUSD.value)
const profitKHR = computed(() => revenueKHR.value - expensesKHR.value)

// Efficiency Bars (relative to revenue)
const budgetEfficiencyUSD = computed(() => {
  if (revenueUSD.value === 0) return 0
  return Math.min(100, Math.round((expensesUSD.value / revenueUSD.value) * 100))
})

const budgetEfficiencyKHR = computed(() => {
  if (revenueKHR.value === 0) return 0
  return Math.min(100, Math.round((expensesKHR.value / revenueKHR.value) * 100))
})

const fetchDashboardData = async () => {
  const userId = projectAuth.currentUser?.uid
  if (!userId) return

  try {
    // 1. Fetch Events
    await getCollectionQuery(
      "events",
      [where("userId", "==", userId), where("status", "==", true)],
      (data) => {
        events.value = data
      },
      null,
      true
    )

    // 2. Fetch All Guests for this user
    await getCollectionQuery(
      "guests",
      [where("userId", "==", userId)],
      (data) => {
        guests.value = data
      },
      null,
      true
    )

    // 3. Fetch All Expenses for this user
    await getCollectionQuery(
      "expenses",
      [where("userId", "==", userId)],
      (data) => {
        expenses.value = data
      },
      null,
      true
    )
  } catch (err) {
    console.error("Dashboard data fetch error:", err)
  }
}

onMounted(() => {
  fetchDashboardData()
})
</script>

<style scoped>
.animate-fade-in-up {
  animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>